import { useCallback, useEffect, useRef, useState } from 'react';
import { BEREICH_NAMEN, stufeMindestens } from '@beholder/shared';
import type { KiGespraech, KiGespraechDetail, KiNachricht, KiSchritt, KiVorschlag } from '@beholder/shared/module/ki';
import { api, modulPfad } from '../../core/api';
import type { ModulSeiteProps } from '../../core/modul';
import { Symbol } from '../../core/symbole';
import { Knopf, datumZeit } from '../../core/ui';
import { ANBIETER_NAME, fragenStrom } from './api';
import { KiText } from './Text';

const merkSchluessel = (p: string) => `beholder:ki:gespraech:${p}`;
function merken(projektId: string, id: string | null) {
  try {
    if (id) localStorage.setItem(merkSchluessel(projektId), id);
    else localStorage.removeItem(merkSchluessel(projektId));
  } catch {
    /* kein Speicher */
  }
}
function gemerkt(projektId: string): string | null {
  try {
    return localStorage.getItem(merkSchluessel(projektId));
  } catch {
    return null;
  }
}

/** Chat-Knopf unten rechts auf jeder Projektseite, mit Gesprächsfenster (auf dem Handy Vollbild). */
export function ChatKnopf({ projekt }: ModulSeiteProps) {
  const [offen, setOffen] = useState(false);
  const [start, setStart] = useState<{ id: string | null; n: number }>({ id: null, n: 0 });

  useEffect(() => {
    const h = (e: Event) => {
      const id = (e as CustomEvent<{ gespraechId: string | null }>).detail?.gespraechId ?? null;
      setStart((s) => ({ id, n: s.n + 1 }));
      setOffen(true);
    };
    window.addEventListener('beholder:ki-oeffnen', h);
    return () => window.removeEventListener('beholder:ki-oeffnen', h);
  }, []);

  if (projekt.kiAnbieter === 'aus') return null;
  return (
    <>
      {!offen && (
        <button type="button" className="ki-knopf" aria-label="KI-Hilfe öffnen" title="KI-Hilfe fragen" onClick={() => setOffen(true)}>
          {Symbol.ki}
        </button>
      )}
      {offen && (
        <ChatFenster
          key={start.n}
          projektId={projekt.id}
          darfFragen={stufeMindestens(projekt.rechte.ki, 'schreiben')}
          startId={start.n ? start.id : undefined}
          schliessen={() => setOffen(false)}
        />
      )}
    </>
  );
}

interface Anzeige extends KiNachricht {
  wartet?: boolean;
}

function ChatFenster({
  projektId,
  darfFragen,
  startId,
  schliessen,
}: {
  projektId: string;
  /** Marc: fragen nur mit Schreibrecht im Bereich „KI-Hilfe“. */
  darfFragen: boolean;
  startId?: string | null;
  schliessen(): void;
}) {
  const [gespraechId, setGespraechId] = useState<string | null>(() => (startId !== undefined ? startId : gemerkt(projektId)));
  const [nachrichten, setNachrichten] = useState<Anzeige[]>([]);
  const [vorschlaege, setVorschlaege] = useState<Record<string, KiVorschlag>>({});
  const [liste, setListe] = useState<KiGespraech[] | null>(null);
  const [zeigeListe, setZeigeListe] = useState(false);
  const [eingabe, setEingabe] = useState('');
  const [status, setStatus] = useState('');
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const unten = useRef<HTMLDivElement>(null);
  const feld = useRef<HTMLTextAreaElement>(null);
  // Wechsel auf das gerade angelegte Gespräch, ohne die Nachrichten neu zu laden
  const ohneLaden = useRef(false);
  function setGespraechIdOhneLaden(id: string) {
    ohneLaden.current = true;
    setGespraechId(id);
  }

  // Gespräch laden
  useEffect(() => {
    merken(projektId, gespraechId);
    if (ohneLaden.current) {
      ohneLaden.current = false;
      return;
    }
    if (!gespraechId) {
      setNachrichten([]);
      setVorschlaege({});
      return;
    }
    let weg = false;
    setLaedt(true);
    api
      .get<KiGespraechDetail>(modulPfad(projektId, 'ki', `gespraeche/${gespraechId}`))
      .then((g) => {
        if (weg) return;
        setNachrichten(g.nachrichten);
        setVorschlaege(Object.fromEntries(g.vorschlaege.map((v) => [v.id, v])));
      })
      .catch(() => !weg && setGespraechId(null))
      .finally(() => !weg && setLaedt(false));
    return () => {
      weg = true;
    };
    // nur beim Wechsel des Gesprächs, nicht nach jeder eigenen Frage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gespraechId === null ? 'neu' : gespraechId, projektId]);

  useEffect(() => {
    unten.current?.scrollIntoView({ block: 'end' });
  }, [nachrichten, status]);
  useEffect(() => {
    feld.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && schliessen();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [schliessen]);

  const listeLaden = useCallback(async () => {
    setListe(await api.get<KiGespraech[]>(modulPfad(projektId, 'ki', 'gespraeche')).catch(() => []));
  }, [projektId]);

  const geladenId = useRef<string | null>(gespraechId);

  async function senden() {
    const text = eingabe.trim();
    if (!text || laeuft) return;
    setFehler('');
    setLaeuft(true);
    setStatus('Frage wird gesendet …');
    const vorlaeufig: Anzeige = { id: `neu-${Date.now()}`, rolle: 'user', text, anbieter: null, schritte: [], erstelltAm: new Date().toISOString(), wartet: true };
    setNachrichten((n) => [...n, vorlaeufig]);
    setEingabe('');
    let fertig = false;
    try {
      await fragenStrom(projektId, gespraechId, text, (e) => {
        if (e.typ === 'gespraech') {
          geladenId.current = e.gespraech.id;
        } else if (e.typ === 'status') setStatus(e.text);
        else if (e.typ === 'schritt') {
          if (e.vorschlag) setVorschlaege((v) => ({ ...v, [e.vorschlag!.id]: e.vorschlag! }));
        } else if (e.typ === 'fertig') {
          fertig = true;
          setNachrichten((n) => [...n.filter((x) => x.id !== vorlaeufig.id), e.frage, e.antwort]);
        } else if (e.typ === 'fehler') {
          setFehler(e.text);
        }
      });
      if (!fertig) {
        setNachrichten((n) => n.filter((x) => x.id !== vorlaeufig.id));
        setEingabe(text);
      }
      // Gespräch-ID erst übernehmen, wenn die Antwort da ist (sonst lädt der Effekt dazwischen neu)
      if (geladenId.current && geladenId.current !== gespraechId) {
        setGespraechIdOhneLaden(geladenId.current);
      }
    } catch (f) {
      setFehler(f instanceof Error ? f.message : 'Fehler');
      setNachrichten((n) => n.filter((x) => x.id !== vorlaeufig.id));
      setEingabe(text);
    } finally {
      setLaeuft(false);
      setStatus('');
      feld.current?.focus();
    }
  }

  async function vorschlagAktion(v: KiVorschlag, was: 'ausfuehren' | 'verwerfen') {
    setFehler('');
    try {
      const neu = await api.post<KiVorschlag>(modulPfad(projektId, 'ki', `vorschlaege/${v.id}/${was}`));
      setVorschlaege((x) => ({ ...x, [neu.id]: neu }));
      if (was === 'ausfuehren') window.dispatchEvent(new CustomEvent('beholder:ki-geaendert', { detail: { bereich: v.bereich } }));
    } catch (f) {
      setFehler(f instanceof Error ? f.message : 'Fehler');
    }
  }

  return (
    <div className="ki-fenster" role="dialog" aria-label="KI-Hilfe">
      <header className="ki-kopf">
        <span className="ki-kopf-symbol">{Symbol.ki}</span>
        <strong className="ki-kopf-titel">KI-Hilfe</strong>
        <span className="abstand" />
        <Knopf
          art="leise"
          klein
          title="Frühere Gespräche"
          onClick={() => {
            setZeigeListe((z) => !z);
            void listeLaden();
          }}
        >
          Verlauf
        </Knopf>
        <Knopf
          art="leise"
          klein
          title="Neues Gespräch"
          disabled={laeuft}
          onClick={() => {
            setZeigeListe(false);
            setGespraechId(null);
            setFehler('');
          }}
        >
          {Symbol.plus} Neu
        </Knopf>
        <button type="button" className="knopf knopf-leise knopf-klein" aria-label="Schließen" onClick={schliessen}>
          ✕
        </button>
      </header>

      {zeigeListe ? (
        <div className="ki-liste">
          {liste === null && <p className="leise">Lädt …</p>}
          {liste?.length === 0 && <p className="leise">Noch keine Gespräche.</p>}
          {liste?.map((g) => (
            <button
              key={g.id}
              type="button"
              className={`ki-liste-eintrag${g.id === gespraechId ? ' aktiv' : ''}`}
              onClick={() => {
                setGespraechId(g.id);
                setZeigeListe(false);
              }}
            >
              <span className="ki-liste-titel">{g.titel}</span>
              <span className="leise klein">{datumZeit(g.geaendertAm)}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="ki-verlauf" aria-live="polite">
          {laedt && <p className="leise">Lädt …</p>}
          {!laedt && nachrichten.length === 0 && darfFragen && (
            <div className="ki-willkommen">
              <p>
                <strong>Frag mich etwas über dieses Projekt.</strong>
              </p>
              <p className="leise">Zum Beispiel: „Was ist diese Woche zu tun?“, „Was haben wir bisher ausgegeben?“ oder „Fasse die letzten Mails zusammen.“</p>
              <p className="leise klein">Ich sehe nur, was du auch sehen darfst. Änderungen schlage ich vor – je nach deinen Rechten führe ich sie auch direkt aus.</p>
            </div>
          )}
          {nachrichten.map((n) => (
            <div key={n.id} className={`ki-nachricht ki-${n.rolle}${n.wartet ? ' ki-wartet' : ''}`}>
              <div className="ki-blase">{n.rolle === 'assistant' ? <KiText text={n.text} /> : <span className="ki-frage">{n.text}</span>}</div>
              {n.schritte.length > 0 && (
                <div className="ki-schritte">
                  {n.schritte.map((s, i) =>
                    s.ergebnis === 'vorschlag' && s.vorschlagId && vorschlaege[s.vorschlagId] ? (
                      <VorschlagKarte key={i} v={vorschlaege[s.vorschlagId]!} beiAktion={vorschlagAktion} />
                    ) : (
                      <SchrittMarke key={i} s={s} />
                    ),
                  )}
                </div>
              )}
              {n.rolle === 'assistant' && n.anbieter && <span className="ki-wer">{ANBIETER_NAME[n.anbieter]}</span>}
            </div>
          ))}
          {laeuft && (
            <div className="ki-nachricht ki-assistant">
              <div className="ki-blase ki-denkt">
                <span className="kreisel" aria-hidden /> {status || 'Denkt nach …'}
              </div>
            </div>
          )}
          <div ref={unten} />
        </div>
      )}

      {fehler && <div className="ki-fehler meldung meldung-fehler">{fehler}</div>}
      {!darfFragen ? (
        <div className="ki-nur-lesen">
          <p>
            <strong>Fragen an die KI-Hilfe</strong> sind in diesem Projekt für dich noch nicht freigeschaltet.
          </p>
          <p className="leise klein">Dafür brauchst du das Schreibrecht im Bereich „KI-Hilfe“. Die Projektleitung kann es dir unter „Mitglieder“ geben.</p>
        </div>
      ) : (
      <form
        className="ki-eingabe"
        onSubmit={(e) => {
          e.preventDefault();
          void senden();
        }}
      >
        <textarea
          ref={feld}
          className="eingabe"
          rows={2}
          maxLength={8000}
          placeholder="Frage stellen …"
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void senden();
            }
          }}
        />
        <Knopf art="haupt" type="submit" disabled={laeuft || !eingabe.trim()}>
          Senden
        </Knopf>
      </form>
      )}
    </div>
  );
}

const SCHRITT_TEXT: Record<KiSchritt['ergebnis'], string> = {
  gelesen: 'nachgesehen',
  ausgefuehrt: 'erledigt',
  vorschlag: 'Vorschlag',
  abgelehnt: 'abgelehnt',
  fehler: 'fehlgeschlagen',
};

function SchrittMarke({ s }: { s: KiSchritt }) {
  return (
    <span className={`ki-schritt ki-schritt-${s.ergebnis}`} title={s.hinweis ?? s.beschreibung}>
      {BEREICH_NAMEN[s.bereich]}: {s.beschreibung} – {SCHRITT_TEXT[s.ergebnis]}
    </span>
  );
}

const VORSCHLAG_STATUS: Record<KiVorschlag['status'], string> = {
  offen: 'wartet auf dich',
  ausgefuehrt: 'ausgeführt',
  verworfen: 'verworfen',
  fehler: 'fehlgeschlagen',
};

export function VorschlagKarte({ v, beiAktion }: { v: KiVorschlag; beiAktion(v: KiVorschlag, was: 'ausfuehren' | 'verwerfen'): Promise<void> }) {
  const [laeuft, setLaeuft] = useState(false);
  const felder = v.eingabe && typeof v.eingabe === 'object' ? Object.entries(v.eingabe as Record<string, unknown>) : [];
  const los = async (was: 'ausfuehren' | 'verwerfen') => {
    setLaeuft(true);
    try {
      await beiAktion(v, was);
    } finally {
      setLaeuft(false);
    }
  };
  return (
    <div className={`ki-vorschlag ki-vorschlag-${v.status}`}>
      <div className="ki-vorschlag-kopf">
        <strong>Vorschlag · {BEREICH_NAMEN[v.bereich]}</strong>
        <span className="marke">{VORSCHLAG_STATUS[v.status]}</span>
      </div>
      <div>{v.beschreibung}</div>
      {felder.length > 0 && (
        <dl className="ki-vorschlag-felder">
          {felder.map(([k, w]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{wertText(w)}</dd>
            </div>
          ))}
        </dl>
      )}
      {v.hinweis && <div className="leise klein">{v.hinweis}</div>}
      {v.status === 'offen' && (
        <div className="reihe">
          <Knopf art="haupt" klein disabled={laeuft} onClick={() => void los('ausfuehren')}>
            Ausführen
          </Knopf>
          <Knopf art="leise" klein disabled={laeuft} onClick={() => void los('verwerfen')}>
            Verwerfen
          </Knopf>
        </div>
      )}
    </div>
  );
}

function wertText(w: unknown): string {
  if (w === null || w === undefined) return '–';
  if (typeof w === 'boolean') return w ? 'ja' : 'nein';
  if (typeof w === 'object') return JSON.stringify(w);
  return String(w);
}
